import { RADAR } from './config';
import type { RadarBlip, RadarState, SimulationState, Vec3 } from './models';

const TAU = Math.PI * 2;

export function createRadarState(): RadarState {
  return { sweepAngle: 0, blips: [] };
}

export function relativeToPlayer(playerPosition: Vec3, playerYaw: number, worldPosition: Vec3): { x: number; z: number } {
  const dx = worldPosition.x - playerPosition.x;
  const dz = worldPosition.z - playerPosition.z;
  const cosine = Math.cos(playerYaw);
  const sine = Math.sin(playerYaw);
  return {
    x: dx * cosine + dz * sine,
    z: -dx * sine + dz * cosine,
  };
}

export function bearingForRelative(relativeX: number, relativeZ: number): number {
  return normalizeAngle(Math.atan2(relativeX, relativeZ));
}

export function sweepCrosses(previous: number, next: number, bearing: number): boolean {
  const travel = normalizeAngle(next - previous);
  const target = normalizeAngle(bearing - previous);
  return target <= travel + Number.EPSILON;
}

export function updateRadar(state: SimulationState, delta: number): void {
  const previousSweep = state.radar.sweepAngle;
  const nextSweep = normalizeAngle(previousSweep + (TAU * delta) / RADAR.revolutionSeconds);
  const previousById = new Map(state.radar.blips.map((blip) => [blip.id, blip]));
  const contacts: Array<{ id: string; kind: RadarBlip['kind']; position: Vec3 }> = [];
  for (const obstacle of state.obstacles) {
    if (Math.hypot(obstacle.position.x - state.player.position.x, obstacle.position.z - state.player.position.z) <= RADAR.range) {
      contacts.push({ id: `cover:${obstacle.id}`, kind: 'cover', position: obstacle.position });
    }
  }
  if (state.primaryThreat) {
    contacts.push({ id: `threat:${state.primaryThreat.id}`, kind: 'threat', position: state.primaryThreat.position });
  }

  state.radar = {
    sweepAngle: nextSweep,
    blips: contacts.map((contact) => {
      const relative = relativeToPlayer(state.player.position, state.player.yaw, contact.position);
      const bearing = bearingForRelative(relative.x, relative.z);
      const prior = previousById.get(contact.id)?.intensity ?? 0;
      const crossed = sweepCrosses(previousSweep, nextSweep, bearing);
      if (crossed && contact.kind === 'threat') state.events.push({ type: 'radar-contact' });
      return {
        id: contact.id,
        kind: contact.kind,
        relativeX: relative.x,
        relativeZ: relative.z,
        intensity: crossed ? 1 : Math.max(0, prior - delta / RADAR.blipPersistence),
      };
    }),
  };
}

function normalizeAngle(angle: number): number {
  return ((angle % TAU) + TAU) % TAU;
}
